import {
  Grid,
  Typography,
  Button,
  Stack,
  Box,
  Paper,
  FormHelperText,
} from "@mui/material";
import {
  type ArrayPath,
  type Control,
  type FieldArray,
  type FieldValues,
  useFieldArray,
} from "react-hook-form";

type Props<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: string;
  label: string;
  disabled?: boolean;
  includeItemSectionFrame: boolean;
  renderItem: (index: number, onRemove: () => void) => React.ReactNode;
  getDefaultValue: () => unknown;
  error?: string;
};

export const CmsArrayField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  disabled = false,
  includeItemSectionFrame,
  renderItem,
  getDefaultValue,
  error,
}: Props<TFieldValues>) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: name as ArrayPath<TFieldValues>,
  });

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      {error && (
        <FormHelperText error sx={{ mt: -0.5, mb: 1 }}>
          {error}
        </FormHelperText>
      )}

      {fields.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {fields.map((field, index) => (
            <Paper key={field.id} sx={{ p: 3, mb: 2 }}>
              <Stack spacing={2}>
                {includeItemSectionFrame && (
                  <Typography variant="subtitle2" color="textSecondary">
                    {label} #{index + 1}
                  </Typography>
                )}
                {renderItem(index, () => remove(index))}
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => remove(index)}
                    disabled={disabled}
                  >
                    Remove
                  </Button>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      <Button
        variant="outlined"
        onClick={() =>
          append(
            getDefaultValue() as FieldArray<
              TFieldValues,
              ArrayPath<TFieldValues>
            >,
          )
        }
        disabled={disabled}
        size="small"
        sx={{ textTransform: "none" }}
      >
        Add
      </Button>
    </Grid>
  );
};
